import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { containerLineDriverAPI } from "../../services/api";
import { useContainerMessages } from "../../hooks/useContainerMessages";
import { ContainerMessageList } from "../containerOrders/ContainerMessageList";
import { ContainerMessageComposer } from "../containerOrders/ContainerMessageComposer";
import type { ContainerMessaging } from "../../types/containerMessages";

/**
 * The driver's half of a container's conversation.
 *
 * A second tab on the execution screen rather than a panel below the stop
 * frame, and that is the whole reason the screen has tabs at all: the frame is
 * built around a driver doing exactly one thing at a stop, and a conversation
 * underneath it would be a second thing, always on screen, always scrolled past.
 * As a tab it is one tap away and completely out of the way until it is wanted
 * — and the badge on that tab is what makes "out of the way" safe.
 *
 * The thread is fetched when this tab is opened and not before. A driver with
 * twelve containers on their truck must not pay twelve requests to be told
 * eleven of them have nothing to say; the counters on the container answer that
 * already, and they arrive with the list.
 */

interface Props {
  lineId: string;
  /** Whether this tab is the one on screen. Drives fetch, room and receipt. */
  open: boolean;
  /** A cancelled container's thread is a record; nothing more is added to it. */
  cancelled?: boolean;
  /** Keeps the tab badge on the page above in step. */
  onMessaging?: (messaging: ContainerMessaging) => void;
}

export function DriverMessagesTab({
  lineId,
  open,
  cancelled = false,
  onMessaging,
}: Props) {
  const { t } = useTranslation("containerOrders");

  /**
   * The four calls, bound to this container.
   *
   * Memoised on the id alone: an object rebuilt every render would make the
   * hook's effects re-run on every keystroke in the composer.
   */
  const transport = useMemo(
    () => ({
      list: (params?: { before?: string; limit?: number }) =>
        containerLineDriverAPI.listMessages(lineId, params),
      send: (
        data: Parameters<typeof containerLineDriverAPI.sendMessage>[1],
      ) => containerLineDriverAPI.sendMessage(lineId, data),
      markRead: () => containerLineDriverAPI.markMessagesRead(lineId),
    }),
    [lineId],
  );

  const {
    messages,
    loading,
    sending,
    error,
    hasMore,
    loadOlder,
    send,
    clearError,
  } = useContainerMessages({
    lineId,
    side: "driver",
    open,
    transport,
    onMessaging,
  });

  const handleSend = useCallback(
    async (data: Parameters<typeof send>[0]) => {
      const ok = await send(data);
      if (!ok) {
        toast.error(error ?? t("messages.sendFailed"));
        clearError();
      }
      return ok;
    },
    [send, error, clearError, t],
  );

  return (
    /**
     * Takes whatever the page has left, rather than measuring it.
     *
     * The execution page becomes a fixed-height column when this tab is open —
     * header, this, and the space reserved for the tab bar — so the height is
     * grown into rather than computed from a hard-coded header size that a
     * longer container number or a second status line would quietly break.
     * `min-h-0` is what lets the thread inside actually scroll: without it a
     * flex child refuses to shrink below its content and the composer is pushed
     * off the bottom.
     */
    <div className="mx-auto flex min-h-0 w-full max-w-md flex-1 flex-col bg-[#F7F6F2]">
      {/* No header of its own. The screen already has one — the container
          number and its progress, two lines above this — and a second bar
          repeating the same container number and naming the only party a driver
          can message was a band of chrome across the top of a phone that says
          nothing the tab bar has not already said. */}
      <ContainerMessageList
        messages={messages}
        side="driver"
        loading={loading}
        hasMore={hasMore}
        onLoadOlder={loadOlder}
        emptyText={t("messages.emptyDriver")}
      />

      <ContainerMessageComposer
        onSend={handleSend}
        sending={sending}
        disabled={cancelled}
        disabledNotice={t("messages.closedCancelled")}
        showQuick
        touch
      />
    </div>
  );
}
